import { useRef, useCallback, useMemo, useState } from 'react';
import { get, isEmpty, omit, times } from 'lodash';

import {
  Card,
  CardBody,
  CardHeader,
  Drawer,
  DrawerBody,
  Icon,
  Skeleton,
  Tooltip,
  UserBadge,
} from '@repo/ui';
import {
  AppealStatus,
  ActivityEventType,
  WriteOffStatus,
  WriteOffType,
  WriteOffCategory,
} from '@repo/types';

import { dateUtil, numberUtil } from '@xtnd/web/shared/utils';

import { buildDiffTree, countChanges } from '../../queries/activityLogQuery';
import { Activity, ActivityChange, DiffNode, DiffType } from '../../types/activity';
import { ArAppealStatus } from './ArAppealStatus';
import { WriteOffStatusBadge } from './WriteOffStatusBadge';
import { ArWriteOffType } from './ArWriteOffType';
import { ArWriteOffCategory } from './ArWriteOffCategory';
import { CHANGE_TYPE_CONFIG, DEFAULT_CHANGE_CONFIG, PAGE_SIZE } from '../../constants/activity';
import { INVOICE_STATUS_LABELS, INVOICE_STATUS_CLASSES } from '../appeals/AppealsColumns';

/**
 * Field configuration for activity log display
 * - display: Optional human-readable label (falls back to camelCaseToWords)
 * - transform: Optional function to transform the value for display (returns ReactNode)
 */
/** Value type for transform functions - can be string, number, boolean, null, or undefined */
type Primitive = string | number | boolean | null | undefined;
type TransformValue = Primitive | TransformValue[] | { [key: string]: TransformValue };

type TransformOptions = {
  isStrikeThrough?: boolean;
};

type FieldConfig = {
  display?: string;
  transform?: (value: TransformValue, options?: TransformOptions) => React.ReactNode;
};

const formatCurrency = (value: TransformValue): string => {
  return numberUtil.formatCurrency(Number(value) || 0);
};

const formatDate = (value: TransformValue): string => {
  try {
    return dateUtil.formatDate(String(value), dateUtil.DateFormat.US);
  } catch {
    return String(value);
  }
};

const formatCapitalize = (value: TransformValue): string => {
  const str = String(value).toLowerCase();
  return str.charAt(0).toUpperCase() + str.slice(1);
};

const formatAppealStatus = (value: TransformValue, options?: TransformOptions): React.ReactNode => {
  return (
    <ArAppealStatus
      status={String(value) as AppealStatus}
      isStrikeThrough={options?.isStrikeThrough}
    />
  );
};

const formatInvoiceStatus = (
  value: TransformValue,
  options?: TransformOptions
): React.ReactNode => {
  const status = String(value);
  const label = get(INVOICE_STATUS_LABELS, status, '-');
  if (label === '-') return '-';

  const className = get(INVOICE_STATUS_CLASSES, status, '');
  const strikeClass = options?.isStrikeThrough ? 'line-through opacity-60' : '';
  return (
    <span
      className={`inline-flex items-center px-2 py-1 rounded-full text-sm ${className} ${strikeClass}`}
    >
      {label}
    </span>
  );
};

const formatWriteOffStatus = (
  value: TransformValue,
  options?: TransformOptions
): React.ReactNode => {
  return (
    <WriteOffStatusBadge
      status={String(value) as WriteOffStatus}
      isStrikeThrough={options?.isStrikeThrough}
    />
  );
};

const formatCategory = (value: TransformValue, options?: TransformOptions): React.ReactNode => {
  return (
    <ArWriteOffCategory
      category={String(value) as WriteOffCategory}
      isStrikeThrough={options?.isStrikeThrough}
    />
  );
};

const formatWriteOffType = (value: TransformValue, options?: TransformOptions): React.ReactNode => {
  return (
    <ArWriteOffType
      type={String(value) as WriteOffType}
      isStrikeThrough={options?.isStrikeThrough}
    />
  );
};

/**
 * Field mapping configuration
 * Only includes fields that need:
 * - A transform function (status badges, currency, dates)
 * - A display label different from camelCase split
 */
const FIELD_CONFIG: Record<string, FieldConfig> = {
  appealStatus: { transform: formatAppealStatus },
  writeOffStatus: { transform: formatWriteOffStatus },
  state: { display: 'Invoice status', transform: formatInvoiceStatus },
  category: { transform: formatCategory },
  writeOffCategory: { transform: formatCategory },
  writeOffType: { display: 'Write-off type', transform: formatWriteOffType },

  total: { display: 'Total amount', transform: formatCurrency },
  paid: { display: 'Amount paid', transform: formatCurrency },
  outstandingBalance: { display: 'Amount due', transform: formatCurrency },
  creditsIssued: { transform: formatCurrency },
  totalFees: { transform: formatCurrency },
  totalExpenses: { transform: formatCurrency },
  outstandingFees: { transform: formatCurrency },
  outstandingExpenses: { transform: formatCurrency },
  amount: { transform: formatCurrency },
  writeOffAmount: { transform: formatCurrency },
  appealedAmount: { transform: formatCurrency },
  remainingAmount: { transform: formatCurrency },
  collectedAmount: { transform: formatCurrency },
  promptPayment: { transform: formatCurrency },
  expensesCut: { transform: formatCurrency },
  administrativeFee: { transform: formatCurrency },
  feesCut: { transform: formatCurrency },
  bulkDiscount: { transform: formatCurrency },

  deadline: { transform: formatDate },
  invoiceDate: { transform: formatDate },
  invoiceDueAt: { display: 'Due date', transform: formatDate },

  matterDisplayNumber: { display: 'Matter' },
  maxDaysPastDue: { display: 'DPD' },
  isCarrierInPreview: { display: 'Carrier in preview' },
  kind: { display: 'Invoice type' },
  emailQueueStatus: { display: 'Email queue status', transform: formatCapitalize },
};

/**
 * Converts camelCase to sentence case with spaces
 * e.g., "daysPastDue" -> "Days past due"
 * Preserves "ID" as uppercase: "invoiceId" -> "Invoice ID"
 */
const camelCaseToWords = (str: string): string => {
  const spaced = str.replace(/([A-Z])/g, ' $1').trim();
  const result = spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();

  return result.replace(/\bid\b/gi, 'ID');
};

/**
 * Gets the display label for a field
 * Falls back to converting camelCase to readable words
 */
const getFieldDisplay = (fieldName: string): string => {
  return FIELD_CONFIG[fieldName]?.display ?? camelCaseToWords(fieldName);
};

/**
 * Transforms a value using the field-specific transformer or default formatting
 * @param options - Styling options (e.g., isStrikeThrough for old/changed values)
 */
const transformValue = (
  fieldName: string,
  value: TransformValue,
  options?: TransformOptions
): React.ReactNode => {
  if (value === null || value === undefined) return '-';
  const config = FIELD_CONFIG[fieldName];

  if (config?.transform) {
    return config.transform(value, options);
  }
  return formatValue(value);
};

/**
 * Renders complex values (arrays and objects) for display.
 */
const renderComplexValue = (value: unknown): React.ReactNode => {
  if (Array.isArray(value)) {
    if (value.length === 0) return '-';
    if (value.length === 1) {
      return typeof value[0] === 'object' ? renderComplexValue(value[0]) : String(value[0]);
    }

    return (
      <ul className="list-disc pl-4 space-y-1">
        {value.map((item, idx) => (
          <li key={idx}>{typeof item === 'object' ? renderComplexValue(item) : String(item)}</li>
        ))}
      </ul>
    );
  }

  if (typeof value === 'object' && value !== null) {
    return (
      <div className="flex flex-col gap-0.5">
        {Object.entries(value).map(([key, val]) => {
          const isComplex =
            typeof val === 'object' && val !== null && (Array.isArray(val) ? val.length > 0 : true);

          return (
            <div
              key={key}
              className={`text-xs ${isComplex ? 'flex flex-col gap-1' : 'flex gap-1'}`}
            >
              <span className="font-medium opacity-70">{camelCaseToWords(key)}:</span>
              <span className={isComplex ? 'pl-3' : ''}>
                {typeof val === 'object' && val !== null ? renderComplexValue(val) : String(val)}
              </span>
            </div>
          );
        })}
      </div>
    );
  }

  return String(value);
};

/**
 * Formats a value for display, handling various types (default transformer)
 */
const formatValue = (value: TransformValue): React.ReactNode => {
  if (isEmptyValue(value)) return '-';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return value.toLocaleString();

  if (Array.isArray(value) || (typeof value === 'object' && value !== null)) {
    return renderComplexValue(value);
  }

  if (typeof value === 'string') {
    // Check if it's an ISO date string
    if (/^\d{4}-\d{2}-\d{2}T/.test(value)) {
      try {
        return dateUtil.formatDate(value, dateUtil.DateFormat.US_WITH_TIME);
      } catch {
        return value;
      }
    }
    return value;
  }
  return String(value);
};

/**
 * Checks if a value is considered "empty" (null, undefined, or empty string)
 */
const isEmptyValue = (value: unknown): boolean => {
  if (value === null || value === undefined || value === '') return true;
  if (Array.isArray(value) && value.length === 0) return true;
  if (typeof value === 'object') {
    return Object.keys(value as object).length === 0;
  }
  return false;
};

/**
 * Determines the effective display type for a change.
 * If type is UPDATED but old is empty, treat as ADDED.
 * If type is UPDATED but new is empty, treat as DELETED.
 */
const getActivityType = (change: ActivityChange): DiffType => {
  if (change.type === DiffType.UPDATED) {
    if (isEmptyValue(change.old)) return DiffType.ADDED;
    if (isEmptyValue(change.new)) return DiffType.DELETED;
  }
  return change.type;
};

/**
 * Renders a leaf change item with icon and formatted value (handling diff styles and transforms)
 */
const LeafChangeRow = ({ node }: { node: DiffNode }) => {
  if (!node.change) return null;

  const { change, key: fieldName } = node;
  const effectiveType = getActivityType(change);
  const config = CHANGE_TYPE_CONFIG[effectiveType] ?? DEFAULT_CHANGE_CONFIG;
  const displayLabel = getFieldDisplay(fieldName);

  const renderValue = () => {
    switch (effectiveType) {
      case DiffType.UPDATED:
        return (
          <>
            <div className="text-xtnd-dark-900 dark:text-xtnd-light min-w-0 line-through opacity-60">
              {transformValue(fieldName, change.old as TransformValue, { isStrikeThrough: true })}
            </div>
            <Icon
              name="FiArrowRight"
              size="sm"
              className="text-xtnd-dark-900 dark:text-xtnd-light"
            />
            <div className="text-xtnd-dark-900 dark:text-xtnd-light min-w-0">
              {transformValue(fieldName, change.new as TransformValue, { isStrikeThrough: false })}
            </div>
          </>
        );
      case DiffType.ADDED:
        return (
          <div className="text-green-600 dark:text-green-400 break-words">
            {transformValue(fieldName, change.new as TransformValue)}
          </div>
        );
      case DiffType.DELETED:
        return (
          <div className="text-red-600 dark:text-red-400 break-words">
            {transformValue(fieldName, (change.old ?? change.new) as TransformValue)}
          </div>
        );
      default:
        return (
          <div className="text-gray-600 dark:text-gray-400 break-words">
            {transformValue(fieldName, (change.new ?? change.old) as TransformValue)}
          </div>
        );
    }
  };

  return (
    <div className="flex items-start gap-3 py-2">
      <div className={`p-1 rounded-full border ${config.bgColorClass} ${config.borderClass}`}>
        <Icon name={config.iconName} size="xs" className={config.iconColorClass} />
      </div>
      <div className="flex-1 min-w-0">
        <span className="text-sm text-xtnd-dark-600 dark:text-xtnd-light-300 tracking-wide">
          {displayLabel}
        </span>
        <div className="flex items-center gap-2 mt-1 text-sm">{renderValue()}</div>
      </div>
    </div>
  );
};

/**
 * Renders a branch node as a Card with header (group name) and body (leaf changes)
 */
const BranchChangeCard = ({ node }: { node: DiffNode }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const PREVIEW_LIMIT = 3;

  if (!node.children) return null;

  // Collect all leaf nodes from children (recursively flatten for display)
  const collectLeaves = (nodes: DiffNode[]): DiffNode[] => {
    const leaves: DiffNode[] = [];
    for (const n of nodes) {
      if (n.change) {
        leaves.push(n);
      }
      if (n.children) {
        leaves.push(...collectLeaves(n.children));
      }
    }
    return leaves;
  };

  const leafNodes = collectLeaves(node.children);
  const displayedNodes = isExpanded ? leafNodes : leafNodes.slice(0, PREVIEW_LIMIT);
  const hasMore = leafNodes.length > PREVIEW_LIMIT;

  return (
    <Card variant="outlined" padding="none" className="overflow-hidden">
      <CardHeader className="px-4 py-2 bg-gray-50 dark:bg-xtnd-dark-900/50">
        <span className="text-sm font-medium text-xtnd-dark-700 dark:text-xtnd-light-200">
          {getFieldDisplay(node.key)}
        </span>
      </CardHeader>
      {leafNodes.length > 0 && (
        <CardBody className="px-4 -mt-3">
          {displayedNodes.map((leaf, idx) => (
            <LeafChangeRow key={`${leaf.key}-${idx}`} node={leaf} />
          ))}
          {hasMore && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="w-full py-2 mt-2 text-xs font-medium text-xtnd-dark-500 dark:text-xtnd-light-400 hover:text-xtnd-dark-700 dark:hover:text-xtnd-light-200 flex items-center justify-center gap-1 transition-colors"
            >
              <span>
                {isExpanded ? 'Show less' : `Show ${leafNodes.length - PREVIEW_LIMIT} more`}
              </span>
              <Icon name={isExpanded ? 'FiChevronUp' : 'FiChevronDown'} size="xs" />
            </button>
          )}
        </CardBody>
      )}
    </Card>
  );
};

/**
 * Renders a single change row - either as a leaf or a branch card
 */
const ChangeRow = ({ node }: { node: DiffNode }) => {
  if (node.children) {
    return <BranchChangeCard node={node} />;
  }

  return <LeafChangeRow node={node} />;
};

interface ActivityLogCardProps {
  activity: Activity;
}

const ActivityTitle = ({
  actorName,
  actorRole,
  eventType,
  category,
}: {
  actorName: string;
  actorRole?: string;
  eventType: ActivityEventType;
  category: string;
}) => {
  const isUpdated = eventType === ActivityEventType.UPDATED;

  return (
    <div className="flex items-center gap-1.5 leading-tight">
      <span className="text-sm text-xtnd-dark-900 dark:text-xtnd-white">{actorName}</span>
      <span className="text-sm">
        {eventType.toLowerCase()} the{' '}
        <span
          className={`font-medium capitalize ${
            category.toLowerCase() === 'invoice'
              ? 'text-indigo-600 dark:text-indigo-400'
              : 'text-orange-600 dark:text-orange-400'
          }`}
        >
          {category}
        </span>
      </span>
      {isUpdated && actorRole && (
        <>
          <span className="text-xtnd-dark-400 dark:text-xtnd-light-400">•</span>
          <span className="text-xs">{actorRole}</span>
        </>
      )}
    </div>
  );
};

export const ActivityLogCard = ({ activity }: ActivityLogCardProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const PREVIEW_LIMIT = 3;

  const fullDate = dateUtil.formatDate(
    new Date(activity.createdAt),
    dateUtil.DateFormat.US_WITH_TIME
  );
  const timeAgo = dateUtil.formatTimeAgo(new Date(activity.createdAt).toISOString());

  const diffNodes = useMemo(() => {
    const nodes = buildDiffTree(activity.diff);

    return [...nodes].sort((a, b) => {
      const aIsLeaf = !!a.change;
      const bIsLeaf = !!b.change;
      if (aIsLeaf && !bIsLeaf) return -1;
      if (!aIsLeaf && bIsLeaf) return 1;
      return 0;
    });
  }, [activity.diff]);

  const changeCount = useMemo(() => countChanges(diffNodes), [diffNodes]);

  const isUpdated = activity.eventType === ActivityEventType.UPDATED;
  const isDeleted = activity.eventType === ActivityEventType.DELETED;

  // Hide entire card for UPDATED events with no changes
  if (isUpdated && changeCount === 0) {
    return null;
  }

  return (
    <li className="relative pl-6 pb-8 last:pb-0">
      <UserBadge
        name={activity.actorName}
        size="xs"
        className="absolute -left-[0.8rem] mt-0.5 -top-[0.2rem]"
      />

      <div className="flex flex-col gap-4">
        <div className="flex justify-between items-center gap-4 text-xtnd-dark-400 dark:text-xtnd-light-400">
          <ActivityTitle
            actorName={activity.actorName ?? 'Unknown'}
            actorRole={activity.actorRole}
            eventType={activity.eventType}
            category={activity.category}
          />
          <div className="flex items-center gap-1 text-xs whitespace-nowrap">
            <Icon name="HiClock" size="xs" />
            <Tooltip
              content={fullDate}
              position="auto"
              className="rounded-md"
              contentClassName="min-w-[11rem]"
            >
              <span>{timeAgo}</span>
            </Tooltip>
          </div>
        </div>

        {!isDeleted && (
          <Card variant="outlined" padding="none" className="overflow-hidden">
            <CardHeader className="px-4 py-2 bg-gray-50 dark:bg-xtnd-dark-900/50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-xtnd-dark-600 dark:text-xtnd-light-300">
                    {changeCount} {changeCount === 1 ? 'change' : 'changes'}
                  </span>
                </div>
              </div>
            </CardHeader>
            <CardBody className="px-4 space-y-3 pb-4">
              {(isExpanded ? diffNodes : diffNodes.slice(0, PREVIEW_LIMIT)).map((node, idx) => (
                <ChangeRow key={`${node.key}-${idx}`} node={node} />
              ))}
              {diffNodes.length > PREVIEW_LIMIT && (
                <button
                  onClick={() => setIsExpanded(!isExpanded)}
                  className="w-full mt-2 text-xs font-medium text-xtnd-dark-500 dark:text-xtnd-light-400 hover:text-xtnd-dark-700 dark:hover:text-xtnd-light-200 flex items-center justify-center gap-1 transition-colors"
                >
                  <span>
                    {isExpanded ? 'Show less' : `Show ${diffNodes.length - PREVIEW_LIMIT} more`}
                  </span>
                  <Icon name={isExpanded ? 'FiChevronUp' : 'FiChevronDown'} size="xs" />
                </button>
              )}
            </CardBody>
          </Card>
        )}
      </div>
    </li>
  );
};

interface ActivityDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  invoiceNumber: string;
  activities: Activity[];
  isInitialLoading: boolean;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  commentHistory?: string;
  writeOffCommentHistory?: string;
}

export const ActivityDrawer = ({
  isOpen,
  onClose,
  invoiceNumber,
  activities,
  isInitialLoading,
  isFetchingNextPage,
  hasNextPage,
  fetchNextPage,
  commentHistory,
  writeOffCommentHistory,
}: ActivityDrawerProps) => {
  type ActivityTab = 'activity' | 'comment';
  const [activeTab, setActiveTab] = useState<ActivityTab>('activity');
  const observer = useRef<IntersectionObserver | null>(null);

  type CommentHistoryItem = {
    text: string;
    actor: string;
    createdAt: string;
    statusChanges?: {
      oldStatus: AppealStatus | WriteOffStatus;
      newStatus: AppealStatus | WriteOffStatus;
    };
  };

  const parseCommentHistory = (raw?: string): CommentHistoryItem[] => {
    if (!raw || raw.trim().length === 0) return [];
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return parsed.map((item: any) => ({
          text: item.text || '',
          actor: item.actor || 'Unknown',
          createdAt: item.createdAt || new Date().toISOString(),
          statusChanges: item.statusChanges,
        }));
      }
      return [];
    } catch {
      return [];
    }
  };

  const historyComments = useMemo(() => {
    // Parse both commentHistory and writeOffCommentHistory
    const invoiceComments = parseCommentHistory(commentHistory);
    const writeOffComments = parseCommentHistory(writeOffCommentHistory);
    
    // Merge and sort by createdAt descending (newest first)
    const allComments = [...invoiceComments, ...writeOffComments];
    return allComments.sort((a, b) => {
      const dateA = new Date(a.createdAt).getTime();
      const dateB = new Date(b.createdAt).getTime();
      return dateB - dateA;
    });
  }, [commentHistory, writeOffCommentHistory]);

  // Remove comment from diff for activity timeline and drop logs that only had comment
  const timelineActivities = useMemo(() => {
    return activities
      .map(act => {
        const trimmedDiff = omit(act.diff ?? {}, [
          'writeOffCommentHistory',
          'commentHistory',
        ]);
        return { ...act, diff: trimmedDiff };
      })
      .filter(act => !isEmpty(act.diff));
  }, [activities]);
  const lastElementRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (isInitialLoading) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver(
        entries => {
          const target = entries[0];
          if (target?.isIntersecting && hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
          }
        },
        { threshold: 0, rootMargin: '200px' }
      );

      if (node) observer.current.observe(node);
    },
    [isInitialLoading, hasNextPage, isFetchingNextPage, fetchNextPage]
  );

  const renderSkeleton = (numOfSkeletons: number) => {
    return (
      <ol className="relative space-y-4">
        {times(numOfSkeletons, idx => (
          <li key={idx} className="relative pl-6 pb-8 last:pb-0">
            <div className="absolute -left-[0.8rem] mt-0.5 -top-[0.2rem]">
              <Skeleton variant="circular" width={24} height={24} />
            </div>

            <div className="flex flex-col gap-4">
              <div className="flex justify-between items-center gap-4">
                <div className="flex items-center gap-1.5">
                  <Skeleton variant="text" width="80px" height="1rem" />
                  <Skeleton variant="text" width="40px" height="0.75rem" />
                </div>
                <Skeleton variant="text" width="120px" height="0.75rem" />
              </div>

              <div className="border border-xtnd-light dark:border-xtnd-dark-700 rounded-lg p-4 space-y-3">
                <Skeleton variant="text" width="60px" height="0.75rem" className="mb-2" />
                <div className="space-y-2">
                  <Skeleton variant="text" width="100%" height="0.75rem" />
                  <Skeleton variant="text" width="80%" height="0.75rem" />
                </div>
              </div>
            </div>
          </li>
        ))}
      </ol>
    );
  };

  const renderTabs = () => {
    return (
      <div className="border-b border-xtnd-light dark:border-xtnd-dark-700 mb-4">
        <div className="flex items-center">
          <button
            type="button"
            className={`px-4 py-2.5 text-sm font-medium transition-colors duration-150 border-b-2 -mb-px ${
              activeTab === 'activity'
                ? 'border-xtnd-blue text-xtnd-dark-900 dark:text-xtnd-white font-semibold'
                : 'border-transparent text-xtnd-dark-500 dark:text-xtnd-light-400 hover:text-xtnd-dark-700 dark:hover:text-xtnd-light-200'
            }`}
            onClick={() => setActiveTab('activity')}
          >
            Activity
          </button>
          <button
            type="button"
            className={`px-4 py-2.5 text-sm font-medium transition-colors duration-150 border-b-2 -mb-px ${
              activeTab === 'comment'
                ? 'border-xtnd-blue text-xtnd-dark-900 dark:text-xtnd-white font-semibold'
                : 'border-transparent text-xtnd-dark-500 dark:text-xtnd-light-400 hover:text-xtnd-dark-700 dark:hover:text-xtnd-light-200'
            }`}
            onClick={() => setActiveTab('comment')}
          >
            Comments
          </button>
        </div>
      </div>
    );
  };

  const renderComment = () => {
    if (isInitialLoading && historyComments.length === 0) {
      return renderSkeleton(5);
    }

    if (historyComments.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center min-h-[40vh] text-xtnd-dark-400">
          <Icon name="HiClock" size="lg" className="mb-2 opacity-20" />
          <p className="text-sm">No comment history found</p>
        </div>
      );
    }

    // Helper function to render comment content
    const renderCommentContent = (currentText: string) => {
      return (
        <div className="py-2 text-sm">
          <div className="text-xtnd-dark-900 dark:text-xtnd-light whitespace-pre-wrap break-words">
            {currentText}
          </div>
        </div>
      );
    };

    return (
      <ol className="relative border-l-2 border-xtnd-light space-y-4">
        {historyComments.map((item, idx) => {
          const nextComment = idx < historyComments.length - 1 ? historyComments[idx + 1] : null;
          const isUpdated = Boolean(nextComment && nextComment.text !== item.text);
          const hasStatusChange =
            item.statusChanges &&
            item.statusChanges.oldStatus !== item.statusChanges.newStatus;

          const commentContent = renderCommentContent(item.text);

          // Common header section
          const headerSection = (
            <div className="flex items-center justify-between gap-2 text-xtnd-dark-400 dark:text-xtnd-light-400">
              <ActivityTitle
                actorName={item.actor}
                actorRole={undefined}
                eventType={isUpdated ? ActivityEventType.UPDATED : ActivityEventType.CREATED}
                category="invoice"
              />
              <div className="flex items-center gap-1 text-xs text-xtnd-dark-500 dark:text-xtnd-light-400">
                <Icon name="HiClock" size="xs" />
                <Tooltip
                  content={dateUtil.formatDate(
                    new Date(item.createdAt),
                    dateUtil.DateFormat.US_WITH_TIME
                  )}
                  position="auto"
                  className="rounded-md"
                  contentClassName="min-w-[11rem]"
                >
                  <span>{dateUtil.formatTimeAgo(item.createdAt)}</span>
                </Tooltip>
              </div>
            </div>
          );

          // If has statusChanges, always show CardHeader with status
          if (item.statusChanges) {
            const writeOffStatusValues = Object.values(WriteOffStatus);
            const isWriteOffStatus =
              typeof item.statusChanges.oldStatus === 'string' &&
              writeOffStatusValues.includes(item.statusChanges.oldStatus as WriteOffStatus);
            const statusLabel = isWriteOffStatus ? 'Write-off status' : 'Appeal status';

            return (
              <li key={`history-${idx}`} className="relative pl-6 pb-6 last:pb-0">
                <UserBadge
                  name={item.actor}
                  size="xs"
                  className="absolute -left-[0.8rem] mt-0.5 -top-[0.1rem]"
                />
                <div className="flex flex-col gap-2">
                  {headerSection}
                  <Card variant="outlined" padding="none" className="overflow-hidden">
                    <CardHeader className="px-4 py-2 bg-gray-50 dark:bg-xtnd-dark-900/50">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-xtnd-dark-600 dark:text-xtnd-light-300 whitespace-nowrap">
                          {statusLabel}
                        </span>
                        <div className="flex items-center gap-1.5 [&_span]:text-xs [&_span]:px-1 [&_span]:py-0.5">
                          {hasStatusChange ? (
                            // Show old → new with arrow and strikethrough
                            isWriteOffStatus ? (
                              <>
                                <WriteOffStatusBadge
                                  status={item.statusChanges.oldStatus as WriteOffStatus}
                                  isStrikeThrough={true}
                                />
                                <Icon
                                  name="FiArrowRight"
                                  size="xs"
                                  className="text-xtnd-dark-500 dark:text-xtnd-light-400"
                                />
                                <WriteOffStatusBadge
                                  status={item.statusChanges.newStatus as WriteOffStatus}
                                />
                              </>
                            ) : (
                              <>
                                <ArAppealStatus
                                  status={item.statusChanges.oldStatus as AppealStatus}
                                  isStrikeThrough={true}
                                />
                                <Icon
                                  name="FiArrowRight"
                                  size="xs"
                                  className="text-xtnd-dark-500 dark:text-xtnd-light-400"
                                />
                                <ArAppealStatus
                                  status={item.statusChanges.newStatus as AppealStatus}
                                />
                              </>
                            )
                          ) : (
                            // No change, just show current status
                            isWriteOffStatus ? (
                              <WriteOffStatusBadge
                                status={item.statusChanges.newStatus as WriteOffStatus}
                              />
                            ) : (
                              <ArAppealStatus
                                status={item.statusChanges.newStatus as AppealStatus}
                              />
                            )
                          )}
                        </div>
                      </div>
                    </CardHeader>
                    <CardBody className="px-4 space-y-3 pb-4">{commentContent}</CardBody>
                  </Card>
                </div>
              </li>
            );
          }

          // No statusChanges, use simple card
          return (
            <li key={`history-${idx}`} className="relative pl-6 pb-6 last:pb-0">
              <UserBadge
                name={item.actor}
                size="xs"
                className="absolute -left-[0.8rem] mt-0.5 -top-[0.1rem]"
              />
              <div className="flex flex-col gap-2">
                {headerSection}
                <div className="flex flex-col gap-2 rounded-lg border border-xtnd-light dark:border-xtnd-dark-700 p-4 bg-white dark:bg-xtnd-dark-900/50 shadow-sm">
                  {commentContent}
                </div>
              </div>
            </li>
          );
        })}
      </ol>
    );
  };

  const renderTimeline = () => {
    if (isInitialLoading && isEmpty(timelineActivities)) {
      return renderSkeleton(5);
    }

    if (isEmpty(timelineActivities) && !isInitialLoading) {
      return (
        <div className="flex flex-col items-center justify-center min-h-[80vh] text-xtnd-dark-400">
          <Icon name="HiClock" size="lg" className="mb-2 opacity-20" />
          <p className="text-sm">No activity found</p>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <ol className="relative border-l-2 border-xtnd-light">
          {timelineActivities.map((activityItem, idx) => (
            <ActivityLogCard key={idx} activity={activityItem} />
          ))}
        </ol>
        {/* Intersection Observer Target */}
        <div ref={lastElementRef} />
        {/* Loading more spinner */}
        {isFetchingNextPage && renderSkeleton(PAGE_SIZE)}

        {/* End of Activity indicator */}
        {!hasNextPage && timelineActivities.length > 0 && !isFetchingNextPage && (
          <div className="flex flex-col items-center">
            <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mb-3">
              <Icon name="FaCheck" size="md" className="text-green-600" />
            </div>
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
              You're all caught up
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              No more activity to show.
            </p>
          </div>
        )}
      </div>
    );
  };

  return (
    <Drawer
      open={isOpen}
      onClose={onClose}
      title={`Activity - Invoice #${invoiceNumber}`}
      showCloseButton
      height="lg"
      width="w-[550px]"
      position="right"
      className="cursor-default dark:bg-xtnd-dark bg-white min-h-[95vh]"
    >
      <DrawerBody>
        <div className="flex flex-col gap-4 text-sm text-xtnd-dark dark:text-xtnd-light after:content-[''] after:block after:w-full after:dark:border-xtnd-dark">
          {renderTabs()}
          {activeTab === 'activity' ? renderTimeline() : renderComment()}
        </div>
      </DrawerBody>
    </Drawer>
  );
};
